import React, { useEffect, useState } from 'react';
import AdminPasswordModal from './AdminPasswordModal';
import AdminLayout from './AdminLayout';
import jwtService from '../services/jwtService';

interface AdminAuthGuardProps {
  children: React.ReactNode;
  onLogout?: () => void;
}

const AdminAuthGuard: React.FC<AdminAuthGuardProps> = ({ children, onLogout }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminData, setAdminData] = useState<any>(null);

  useEffect(() => {
    // Check if admin is already authenticated with JWT token
    const checkAuthStatus = () => {
      const adminData = jwtService.getAdminToken();
      
      if (adminData) {
        // Token exists and is not expired (checked by jwtService)
        setAdminData(adminData);
        setIsAuthenticated(true);
      } else {
        // No valid token, show login modal
        setShowPasswordModal(true);
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const handlePasswordSuccess = (adminData: any) => {
    // Store admin data using JWT service
    jwtService.storeAdminToken(adminData);
    setAdminData(adminData);
    setIsAuthenticated(true);
    setShowPasswordModal(false);
  };

  const handlePasswordClose = () => {
    // If user closes modal without authentication, redirect to home
    window.location.href = '/';
  };

  const handleLogout = () => {
    // Clear admin data using JWT service
    jwtService.clearAdminToken();
    setAdminData(null);
    setIsAuthenticated(false);
    // Call parent logout handler if provided
    if (onLogout) {
      onLogout();
    }
    // Redirect to home page
    window.location.href = '/';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white/90 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <AdminPasswordModal
          isOpen={showPasswordModal}
          onClose={handlePasswordClose}
          onSuccess={handlePasswordSuccess}
        />
        <div className="min-h-screen bg-white/90 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🔒</span>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600">Please authenticate to access admin panel</p>
          </div>
        </div>
      </>
    );
  }

  // Clone children and pass adminData as props
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { adminData } as any);
    }
    return child;
  });

  return (
    <AdminLayout adminData={adminData} onLogout={handleLogout}>
      {childrenWithProps}
    </AdminLayout>
  );
};

export default AdminAuthGuard;
